import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { getFavoriteTracks } from '@/lib/favoritesService';
import type { Track } from './useMusicLibrary';

export function useFavoriteTracks(
  userId: string,
  selectedAlbumId: string | null,
  tracks: Track[],
  hiddenTrackIds: string[]
) {
  const [favoriteTracksWithCounts, setFavoriteTracksWithCounts] = useState<
    Track[]
  >([]);

  useEffect(() => {
    if (selectedAlbumId !== 'favorites') return;

    async function loadFavoriteTracksWithCounts() {
      try {
        const { tracks: favoriteIds } = await getFavoriteTracks(userId);

        const { data: sessions } = await supabase
          .from('listening_sessions')
          .select('track_id')
          .eq('user_id', userId)
          .eq('completed', true)
          .in('track_id', favoriteIds);

        const playCountMap = new Map<string, number>();
        sessions?.forEach((session: { track_id: string }) => {
          playCountMap.set(
            session.track_id,
            (playCountMap.get(session.track_id) || 0) + 1
          );
        });

        const favoriteTracks = tracks
          .filter(
            t =>
              favoriteIds.includes(t.trackId) &&
              !hiddenTrackIds.includes(t.trackId)
          )
          .map(t => ({
            ...t,
            playCount: playCountMap.get(t.trackId) || 0,
          }))
          .sort((a, b) => (b.playCount || 0) - (a.playCount || 0));

        setFavoriteTracksWithCounts(favoriteTracks);
      } catch (error) {
        console.error('Error loading favorite tracks:', error);
        setFavoriteTracksWithCounts([]);
      }
    }

    loadFavoriteTracksWithCounts();
  }, [selectedAlbumId, userId, tracks, hiddenTrackIds]);

  return favoriteTracksWithCounts;
}
